import * as signalR from '@microsoft/signalr';
import { getToken } from '@/utils/common';
import {
  NotificationChangedRealtimePayload,
  NotificationRealtimePayload,
} from '@/types/notification';

const HUB_PATH = '/hubs/realtime';
const SUBSCRIBE_METHOD = 'SubscribeAsync';
const UNSUBSCRIBE_METHOD = 'UnsubscribeAsync';
const COMMENT_NOTIFICATION_EVENT = 'CommentNotificationChanged';
const HEADER_NOTIFICATION_EVENT = 'NotificationChanged';

type NotificationHandlers = {
  onCommentNotificationChanged?: (payload: NotificationRealtimePayload) => void;
  onNotificationChanged?: (payload: NotificationChangedRealtimePayload) => void;
};

const resolveHubUrl = () => {
  const apiUrl = import.meta.env.VITE_API_URL;

  if (!apiUrl) {
    throw new Error('Missing VITE_API_URL. Cannot initialize realtime notification hub.');
  }

  return new URL(HUB_PATH, apiUrl).toString();
};

export const buildNotificationTopic = (userId: string) =>
  `notification:user:${userId.replace(/-/g, '')}`;

class RealtimeNotificationService {
  private connection: signalR.HubConnection | null = null;
  private currentTopic: string | null = null;
  private startPromise: Promise<void> | null = null;
  private handlers: NotificationHandlers = {};

  private createConnection() {
    if (this.connection) return this.connection;

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(resolveHubUrl(), {
        accessTokenFactory: () => getToken() ?? '',
      })
      .withAutomaticReconnect()
      .build();

    this.connection.on(COMMENT_NOTIFICATION_EVENT, (payload: NotificationRealtimePayload) => {
      this.handlers.onCommentNotificationChanged?.(payload);
    });

    this.connection.on(HEADER_NOTIFICATION_EVENT, (payload: NotificationChangedRealtimePayload) => {
      this.handlers.onNotificationChanged?.(payload);
    });

    this.connection.onreconnected(async () => {
      if (this.currentTopic) {
        await this.subscribe(this.currentTopic);
      }
    });

    return this.connection;
  }

  setNotificationHandlers(handlers: NotificationHandlers) {
    this.handlers = handlers;
  }

  async start(userId: string) {
    if (!userId) return;

    const topic = buildNotificationTopic(userId);
    const connection = this.createConnection();

    if (connection.state === signalR.HubConnectionState.Connected && this.currentTopic === topic) {
      return;
    }

    if (connection.state === signalR.HubConnectionState.Connected && this.currentTopic && this.currentTopic !== topic) {
      await this.unsubscribe(this.currentTopic);
      this.currentTopic = null;
    }

    if (connection.state === signalR.HubConnectionState.Disconnected) {
      this.startPromise ??= connection
        .start()
        .catch((error) => {
          this.startPromise = null;
          throw error;
        })
        .then(() => {
          this.startPromise = null;
        });
      await this.startPromise;
    } else if (this.startPromise) {
      await this.startPromise;
    }

    if (this.currentTopic !== topic) {
      await this.subscribe(topic);
      this.currentTopic = topic;
    }
  }

  async restart(userId: string) {
    await this.stop();
    await this.start(userId);
  }

  async stop() {
    if (!this.connection) return;

    try {
      if (this.currentTopic) {
        await this.unsubscribe(this.currentTopic);
      }
    } finally {
      this.currentTopic = null;

      if (this.connection.state !== signalR.HubConnectionState.Disconnected) {
        await this.connection.stop();
      }
    }
  }

  async subscribe(topic: string) {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) return;
    await this.connection.invoke(SUBSCRIBE_METHOD, topic);
  }

  async unsubscribe(topic: string) {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) return;

    try {
      await this.connection.invoke(UNSUBSCRIBE_METHOD, topic);
    } catch (error) {
      console.error('Failed to unsubscribe notification topic:', error);
    }
  }
}

export const realtimeNotificationService = new RealtimeNotificationService();
